/**
 * Login.tsx — "Sign in with Microsoft" (Entra OIDC) and/or password → MANDATORY
 * WebAuthn assertion (SPEC §2). Presentation: a two-column hero card.
 *
 * Flow (password path):
 *   1. POST /auth/login { email, password }.
 *      - tokens directly (first-run enrollment window), or
 *      - { webauthn_required, challenge_id }.
 *   2. POST /auth/webauthn/login/begin → assertion options.
 *   3. navigator.credentials.get() → POST /auth/webauthn/login/finish → tokens.
 *
 * No token issuance without a passed assertion unless the account has no
 * credential yet (SPEC §2 / §11.6). OIDC is the sole option when oidc_only is set.
 */

import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useApi } from '@/api/provider';
import { useAuthStore } from '@/auth/store';
import { createCredential, getAssertion, webauthnSupported } from '@/auth/webauthn';
import { ApiError } from '@/api/client';
import type { AuthConfig } from '@/types';

// Server-side route that kicks off the Entra OIDC authorize redirect
// (handlers_oidc.go GET /api/v1/auth/oidc/start). A full-page navigation — not a
// fetch — so the browser follows the 302 chain to Microsoft and back.
const OIDC_START_URL = '/api/v1/auth/oidc/start';

export default function Login() {
  const api = useApi();
  const setTokens = useAuthStore((s) => s.setTokens);
  const navigate = useNavigate();
  const location = useLocation();
  const from = (location.state as { from?: string } | null)?.from ?? '/devices';

  const [orgSlug, setOrgSlug] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [authConfig, setAuthConfig] = useState<AuthConfig | null>(null);

  // Probe which sign-in methods this deployment offers. If the call fails we
  // leave authConfig null and fall back to the password/WebAuthn form so a
  // config-endpoint hiccup never locks everyone out.
  useEffect(() => {
    let active = true;
    api
      .getAuthConfig()
      .then((cfg) => {
        if (active) setAuthConfig(cfg);
      })
      .catch(() => {
        if (active) setAuthConfig({ oidc_enabled: false, oidc_only: false });
      });
    return () => {
      active = false;
    };
  }, [api]);

  const oidcEnabled = authConfig?.oidc_enabled ?? false;
  const oidcOnly = authConfig?.oidc_only ?? false;
  const showPasswordForm = !oidcOnly;

  function signInWithMicrosoft() {
    window.location.href = OIDC_START_URL;
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      // The server resolves the tenant by org slug (handlers_auth.go loginRequest).
      const result = await api.login(orgSlug, email, password);

      // First-run enrollment (no passkey yet): the server returns a short-lived,
      // registration-ONLY token (codex High #5). Enroll a passkey NOW with that
      // token; register/finish returns the full console tokens. The register token
      // is never persisted as a session token.
      if (result.passkey_registration_required && result.register_token) {
        if (!webauthnSupported()) {
          throw new Error('This browser does not support WebAuthn. A passkey or security key is required to finish setting up your account.');
        }
        const tokens = await enrollFirstPasskey(result.register_token);
        setTokens(tokens);
        navigate(from, { replace: true });
        return;
      }

      // Back-compat: an older server returned console tokens directly on first run.
      if (result.access && result.refresh) {
        setTokens({ access: result.access, refresh: result.refresh });
        navigate(from, { replace: true });
        return;
      }

      // Mandatory WebAuthn step-up.
      if (!webauthnSupported()) {
        throw new Error('This browser does not support WebAuthn. A security key or platform authenticator is required.');
      }
      const challengeId = result.challenge_id;
      if (!challengeId) throw new Error('Server did not return a WebAuthn challenge.');

      // Prefer the password-VERIFIED challenge the login response returned inline
      // (true 2FA). Only fall back to the standalone begin for an older server that
      // didn't return options — that path is password-less and the server rejects
      // it unless the deployment opted into passkey-only.
      let challengeToFinish = challengeId;
      let options = result.public_key?.publicKey;
      if (!options) {
        const begin = await api.webauthnLoginBegin(orgSlug, email);
        challengeToFinish = begin.challenge_id;
        options = begin.public_key.publicKey;
      }
      const assertion = await getAssertion(options);
      const tokens = await api.webauthnLoginFinish(challengeToFinish, assertion);
      setTokens(tokens);
      navigate(from, { replace: true });
    } catch (err) {
      setError(humanize(err));
    } finally {
      setBusy(false);
    }
  }

  // First-run passkey enrollment: run the WebAuthn create() ceremony authorized by
  // the short-lived registration token, then return the full console tokens the
  // server mints on first-passkey registration (codex High #5).
  async function enrollFirstPasskey(registerToken: string) {
    const begin = await api.webauthnRegisterBegin(registerToken);
    const credential = await createCredential(begin.public_key.publicKey);
    const res = await api.webauthnRegisterFinish(begin.challenge_id, credential, registerToken);
    if (!res.access || !res.refresh) {
      throw new Error('Passkey registered, but the server did not return a session. Please sign in again.');
    }
    return { access: res.access, refresh: res.refresh };
  }

  return (
    <div className="relative min-h-screen overflow-hidden bg-helm-bg p-4 sm:p-6">
      {/* Ambient background glow. */}
      <div className="pointer-events-none absolute inset-0 opacity-70 [background:radial-gradient(60rem_40rem_at_85%_-10%,rgba(79,140,247,0.18),transparent),radial-gradient(48rem_38rem_at_-10%_110%,rgba(46,204,143,0.10),transparent)]" />

      <div className="relative grid min-h-[calc(100vh-2rem)] place-items-center">
        <div className="w-full max-w-4xl overflow-hidden rounded-2xl border border-helm-border bg-helm-panel shadow-soft">
          <div className="grid grid-cols-1 lg:grid-cols-2">
            {/* ---- Form column ---- */}
            <div className="p-8 sm:p-10">
              <div className="mb-8 flex items-center gap-3">
                <HelmMark />
                <div>
                  <div className="text-lg font-bold leading-tight text-helm-text">Helm</div>
                  <div className="text-xs text-helm-muted">Technician Console</div>
                </div>
              </div>

              <h1 className="mb-1 text-xl font-bold text-helm-text">Sign in</h1>
              <p className="mb-7 text-sm text-helm-muted">
                {oidcOnly
                  ? 'Use your organization Microsoft account to continue.'
                  : 'Welcome back — choose how you’d like to sign in.'}
              </p>

              {oidcEnabled && (
                <button type="button" onClick={signInWithMicrosoft} className="rh-btn-ghost w-full py-2.5">
                  <MicrosoftLogo />
                  Sign in with Microsoft
                </button>
              )}

              {oidcEnabled && showPasswordForm && (
                <div className="my-5 flex items-center gap-3 text-[11px] uppercase tracking-wide text-helm-muted">
                  <span className="h-px flex-1 bg-helm-border" />
                  or continue with email
                  <span className="h-px flex-1 bg-helm-border" />
                </div>
              )}

              {error && (
                <div role="alert" className="mb-4 rounded-lg border border-helm-bad/40 bg-helm-bad/10 px-3 py-2 text-sm text-helm-bad">
                  {error}
                </div>
              )}

              {showPasswordForm && (
                <form onSubmit={onSubmit} className="space-y-4">
                  <div>
                    <label className="mb-1.5 block text-xs font-medium text-helm-muted" htmlFor="org">
                      Organization
                    </label>
                    <input
                      id="org"
                      type="text"
                      autoComplete="organization"
                      required
                      placeholder="your-org-slug"
                      value={orgSlug}
                      onChange={(e) => setOrgSlug(e.target.value)}
                      className="rh-input"
                    />
                  </div>
                  <div>
                    <label className="mb-1.5 block text-xs font-medium text-helm-muted" htmlFor="email">
                      Email
                    </label>
                    <input
                      id="email"
                      type="email"
                      autoComplete="username"
                      required
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="rh-input"
                    />
                  </div>
                  <div>
                    <label className="mb-1.5 block text-xs font-medium text-helm-muted" htmlFor="password">
                      Password
                    </label>
                    <input
                      id="password"
                      type="password"
                      autoComplete="current-password"
                      required
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="rh-input"
                    />
                  </div>
                  <button type="submit" disabled={busy} className="rh-btn-primary w-full py-2.5">
                    {busy ? 'Authenticating…' : 'Sign in with security key'}
                  </button>
                  <p className="text-center text-xs text-helm-muted">
                    WebAuthn/FIDO2 is mandatory — you’ll be prompted for your passkey or security key after your password.
                  </p>
                </form>
              )}
            </div>

            {/* ---- Hero column (hidden on small screens) ---- */}
            <div className="relative hidden overflow-hidden lg:block [background:linear-gradient(135deg,#3b6fd6_0%,#2a3a73_45%,#13151b_100%)]">
              <div className="pointer-events-none absolute -right-16 -top-16 h-64 w-64 rounded-full bg-white/10 blur-3xl" />
              <div className="pointer-events-none absolute -bottom-20 -left-10 h-64 w-64 rounded-full bg-helm-good/10 blur-3xl" />
              <div className="relative flex h-full flex-col justify-between p-10">
                <HelmMark large />
                <div>
                  <h2 className="text-2xl font-bold leading-snug text-white">
                    Secure remote access
                    <br />
                    for your entire fleet.
                  </h2>
                  <p className="mt-3 max-w-xs text-sm text-white/70">
                    End-to-end encrypted sessions, multi-tenant isolation, and passwordless sign-in — operated on your own
                    infrastructure.
                  </p>
                  <ul className="mt-6 space-y-2.5 text-sm text-white/90">
                    <HeroPoint>End-to-end encrypted screen control</HeroPoint>
                    <HeroPoint>Microsoft 365 &amp; WebAuthn sign-in</HeroPoint>
                    <HeroPoint>Per-tenant isolation &amp; audit trail</HeroPoint>
                  </ul>
                </div>
                <div className="text-xs text-white/50">© {new Date().getFullYear()} Helm</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/** Helm mark — a ship's-helm glyph on an accent-gradient tile. */
function HelmMark({ large = false }: { large?: boolean }) {
  const tile = large ? 'h-12 w-12 rounded-2xl' : 'h-10 w-10 rounded-xl';
  const sz = large ? 28 : 22;
  return (
    <div className={`grid ${tile} place-items-center bg-gradient-to-br from-helm-accent to-helm-accentDim shadow-glow`}>
      <svg width={sz} height={sz} viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="1.6" strokeLinecap="round">
        <circle cx="12" cy="12" r="8.5" />
        <circle cx="12" cy="12" r="2.3" fill="white" stroke="none" />
        <path d="M12 3.5v4 M12 16.5v4 M3.5 12h4 M16.5 12h4 M5.9 5.9l2.8 2.8 M15.3 15.3l2.8 2.8 M18.1 5.9l-2.8 2.8 M8.7 15.3l-2.8 2.8" />
      </svg>
    </div>
  );
}

function HeroPoint({ children }: { children: ReactNode }) {
  return (
    <li className="flex items-center gap-2.5">
      <svg
        width="16"
        height="16"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.6"
        strokeLinecap="round"
        strokeLinejoin="round"
        className="shrink-0 text-helm-good"
        aria-hidden="true"
      >
        <path d="M20 6 9 17l-5-5" />
      </svg>
      {children}
    </li>
  );
}

/** Microsoft four-square mark (brand colors are intentionally hard-coded). */
function MicrosoftLogo() {
  return (
    <svg width="18" height="18" viewBox="0 0 21 21" aria-hidden="true">
      <rect x="1" y="1" width="9" height="9" fill="#f25022" />
      <rect x="11" y="1" width="9" height="9" fill="#7fba00" />
      <rect x="1" y="11" width="9" height="9" fill="#00a4ef" />
      <rect x="11" y="11" width="9" height="9" fill="#ffb900" />
    </svg>
  );
}

function humanize(err: unknown): string {
  if (err instanceof ApiError) {
    if (err.status === 401) return 'Invalid email or password.';
    return err.message;
  }
  if (err instanceof DOMException && err.name === 'NotAllowedError') {
    return 'Security-key prompt was dismissed or timed out. Try again.';
  }
  if (err instanceof Error) return err.message;
  return 'Sign-in failed.';
}
